import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Search,
  Scale,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  Calendar,
  Filter,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { log } from "../lib/log";

const TOPIC_LABELS_AR = {
  narcotics: "مخدرات",
  forgery: "تزوير",
  labor: "عمل وموظفين",
  military: "عسكريون",
  rental: "إيجارات",
  copyright: "حق المؤلف",
  civil: "مدني",
  commercial: "تجاري",
  personal: "أحوال شخصية",
  criminal: "جنايات",
  other: "متنوّع",
};
const TOPIC_LABELS_EN = {
  narcotics: "Narcotics",
  forgery: "Forgery",
  labor: "Labour",
  military: "Military",
  rental: "Rentals",
  copyright: "Copyright",
  civil: "Civil",
  commercial: "Commercial",
  personal: "Personal status",
  criminal: "Criminal",
  other: "Other",
};

export default function StaffPrinciples() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [query, setQuery] = useState("");
  const [topic, setTopic] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [fullCache, setFullCache] = useState({});

  const t = useMemo(() => ({
    eyebrow: isAr ? "موسوعة مبادئ التمييز" : "Cassation Principles Atlas",
    title: isAr
      ? "قواعد محكمة التمييز المرفوعة، جاهزة للاستشهاد."
      : "The cassation rulings library — ready for citation.",
    lead: isAr
      ? "مرجع مغلق على فريق مكتب الدخيل القانونية. تَصفّح القواعد المستخرَجة من 19 حكمًا قضائيًا حسب الموضوع، وانسخ المبدأ المناسب مباشرةً إلى مذكرتك. لا يظهر شيء من هذا للزوار العامة."
      : "An in-house atlas restricted to the Aldakheel legal team. Browse the principles extracted from 19 rulings by topic and copy the relevant rule straight into your memo. Nothing here is exposed to the public.",
    searchPlaceholder: isAr ? "ابحث بالكلمات المفتاحية..." : "Search keywords…",
    allTopics: isAr ? "كل المواضيع" : "All topics",
    empty: isAr ? "لا توجد أحكام مطابقة." : "No matching rulings.",
    loading: isAr ? "تحميل القواعد..." : "Loading principles…",
    expand: isAr ? "عرض النص الكامل" : "Show full text",
    collapse: isAr ? "إخفاء" : "Collapse",
    cassation: isAr ? "محكمة التمييز" : "Court of Cassation",
    rulingsCount: (n) => isAr ? `${n} حكم` : `${n} ruling${n === 1 ? "" : "s"}`,
    sourceLabel: isAr ? "رقم القضية" : "Case",
    courtLabel: isAr ? "المحكمة" : "Court",
    summary: isAr ? "ملخص المبدأ" : "Principle summary",
    fullText: isAr ? "نص الحكم الكامل (مُلصق للاقتباس)" : "Full ruling text (for quoting)",
    forbidden: isAr ? "غير مخوّل لعرض هذه الصفحة" : "Access denied",
  }), [isAr]);

  const labels = isAr ? TOPIC_LABELS_AR : TOPIC_LABELS_EN;

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/staff/judgments/principles");
      setItems(data);
    } catch (e) {
      // Non-critical — empty list on failure is acceptable UX.
      log.debug("StaffPrinciples load failed", e?.message);
      setItems([]);
    } finally {
      setBusy(false);
    }
    // `api`, `setBusy`, `setItems` are all stable refs. No deps required.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && (user.role === "staff" || user.role === "admin")) load();
  }, [user, load]);

  const allTopics = useMemo(() => {
    const set = new Set();
    items.forEach((it) => (it.topics || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (topic !== "all" && !(it.topics || []).includes(topic)) return false;
      if (!q) return true;
      const hay = `${it.case_number || ""} ${it.court || ""} ${it.summary || ""} ${it.text_excerpt || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, topic]);

  const toggleOpen = async (id) => {
    if (openId === id) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    if (!fullCache[id]) {
      try {
        const { data } = await api.get(`/staff/judgments/${id}/full`);
        setFullCache((c) => ({ ...c, [id]: data.text || "" }));
      } catch {
        setFullCache((c) => ({ ...c, [id]: "—" }));
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "staff" && user.role !== "admin") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500 text-sm">
        {t.forbidden}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20" dir={isAr ? "rtl" : "ltr"}>
      <div className="grid lg:grid-cols-12 gap-12 mb-12">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1
            data-testid="staff-principles-title"
            className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight"
          >
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="grid md:grid-cols-12 gap-3 mb-10">
        <div className="md:col-span-8 relative">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            data-testid="principles-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            className="ps-10 h-11 rounded-sm"
          />
        </div>
        <div className="md:col-span-4">
          <Select value={topic} onValueChange={setTopic}>
            <SelectTrigger data-testid="principles-topic-filter" className="h-11 rounded-sm">
              <Filter className="w-3.5 h-3.5 me-2 text-slate-400" />
              <SelectValue placeholder={t.allTopics} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allTopics}</SelectItem>
              {allTopics.map((t) => (
                <SelectItem key={t} value={t}>
                  {labels[t] || t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-6 text-sm text-slate-500">
        {t.rulingsCount(filtered.length)}
      </div>

      {busy ? (
        <div className="py-20 text-center text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin inline-block me-2" />
          {t.loading}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-slate-400 text-sm">
          {t.empty}
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((it) => {
            const open = openId === it.id;
            return (
              <div
                key={it.id}
                data-testid={`principle-card-${it.id}`}
                className="border border-slate-200 bg-white p-5 lg:p-7"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[60%]">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Scale className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                        {it.court || t.cassation}
                      </span>
                      {it.date_issued && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-slate-400">
                          <Calendar className="w-3 h-3" />
                          {it.date_issued}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-xl lg:text-2xl tracking-tight">
                      {it.case_number || "—"}
                    </h3>
                    {it.summary && (
                      <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                        {it.summary}
                      </p>
                    )}
                    {(it.topics || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {(it.topics || []).map((tp) => (
                          <span
                            key={tp}
                            className="text-[10px] uppercase tracking-[0.18em] text-slate-500 bg-slate-50 px-2 py-1"
                          >
                            {labels[tp] || tp}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleOpen(it.id)}
                    data-testid={`principle-toggle-${it.id}`}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900 transition-colors whitespace-nowrap"
                  >
                    {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {open ? t.collapse : t.expand}
                  </button>
                </div>

                {open && (
                  <div
                    data-testid={`principle-full-${it.id}`}
                    className="mt-5 pt-5 border-t border-slate-100"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                        {t.fullText}
                      </span>
                    </div>
                    <pre className="text-[13px] leading-7 whitespace-pre-wrap font-sans text-slate-700 max-h-[60vh] overflow-y-auto bg-slate-50 p-4 rounded-sm">
                      {fullCache[it.id] || it.text_excerpt || "—"}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
